/**
 * MobileBottomBar — Persistent fixed bottom navigation bar for mobile.
 * Shows up to 4 icon+label tabs and a primary CTA button.
 * Automatically hidden at the lg breakpoint (≥1024px).
 */

import * as React from 'react';
import { cn } from '../../../utils/cn';
import { Button } from '../../button';
import styles from './global-header.module.css';

export interface BottomBarItem {
  icon: React.ReactNode;
  label: string;
  href: string;
  active?: boolean;
  onClick?: () => void;
}

export interface MobileBottomBarProps {
  /** Up to 4 tab items. Extras beyond 4 are ignored. */
  items: BottomBarItem[];
  cta: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  className?: string;
}

export function MobileBottomBar({ items, cta, className }: MobileBottomBarProps) {
  return (
    <nav
      aria-label="Mobile bottom navigation"
      className={cn(styles.bottomBar, className)}
    >
      {items.slice(0, 4).map((item, i) => (
        <a
          key={i}
          href={item.href}
          className={styles.bottomBarTab}
          aria-current={item.active ? 'page' : undefined}
          aria-label={item.label}
          onClick={item.onClick}
        >
          <span className={styles.bottomBarIcon} aria-hidden="true">
            {item.icon}
          </span>
          <span className={styles.bottomBarLabel}>{item.label}</span>
        </a>
      ))}

      {cta.href ? (
        <Button variant="primary" size="sm" className={styles.bottomBarCta} asChild>
          <a href={cta.href}>{cta.label}</a>
        </Button>
      ) : (
        <Button
          variant="primary"
          size="sm"
          className={styles.bottomBarCta}
          onClick={cta.onClick}
        >
          {cta.label}
        </Button>
      )}
    </nav>
  );
}

MobileBottomBar.displayName = 'MobileBottomBar';
